import { EMPTY, auditTime, catchError, defer, filter, firstValueFrom, from, groupBy, mergeMap, of, switchMap, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import { releaseFileHandles } from '../../../Services/OrderWizard/fileHandles.ts'
import { wizardUploads } from '../../../Services/OrderWizard/uploadManager.ts'
import { loadDrafts, removeDraft, saveDraft } from '../../../Services/OrderWizard/draftStorage.ts'
import { reconcileDraft } from '../../../Services/OrderWizard/reconcile.ts'
import { guarded } from './guarded.ts'
import { fetchOrderState } from './orderState.ts'
import { draftIdOf } from './draftIdOf.ts'
import type { RootState, WizardEpic } from './draftState.ts'
import { draftOf } from './draftState.ts'

const userIdOf = (state: RootState): string | undefined => state.authentication.user?.id

export const restoreDraftsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.restoreRequested.match),
    withLatestFrom(state$),
    mergeMap(
      guarded('restoring drafts', ([, state]) => {
        const userId = userIdOf(state)
        const { drafts, unrestorable } = userId ? loadDrafts(userId) : { drafts: [], unrestorable: [] }
        return of(
          wizard.draftsRestored({ drafts, unrestorable }),
          ...drafts.filter((draft) => draft.orderId).map((draft) => wizard.reconcileRequested({ draftId: draft.id }))
        )
      })
    )
  )

/**
 * Server state wins; the comparison runs against the draft as it is when the answer arrives, not when it
 * was asked. A newer request for the same draft drops the older one's answer, which may describe the
 * order as it was before.
 */
export const reconcileDraftEpic: WizardEpic = (action$, state$, repositories) =>
  action$.pipe(
    filter(wizard.reconcileRequested.match),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        switchMap(
          guarded('reconciling a draft with its order', (action: ReturnType<typeof wizard.reconcileRequested>) =>
            defer(() => firstValueFrom(state$)).pipe(
              mergeMap((state) => {
                const orderId = draftOf(state, action.payload.draftId)?.orderId
                if (!orderId) {
                  return EMPTY
                }
                return from(fetchOrderState(orderId, repositories)).pipe(
                  withLatestFrom(state$),
                  mergeMap(([{ snapshot }, latest]) => {
                    const draft = draftOf(latest, action.payload.draftId)
                    if (!draft) {
                      return EMPTY
                    }
                    const outcome = reconcileDraft(draft, snapshot)
                    return outcome.kind === 'keep'
                      ? of(wizard.draftReconciled({ draft: outcome.draft }))
                      : of(wizard.draftDiscarded({ draftId: draft.id, reason: outcome.reason }))
                  }),
                  catchError((error: unknown) => {
                    console.warn('Order wizard: could not compare the restored draft with its order', error)
                    return EMPTY
                  })
                )
              })
            )
          )
        )
      )
    )
  )

const SAVE_INTERVAL_MS = 400

/* actions that change nothing a stored draft keeps: progress and in-flight uploads are stored as to re-add */
const NOT_PERSISTED = [
  wizard.draftDiscarded,
  wizard.uploadStarted,
  wizard.uploadProgressed,
  wizard.dropSettled,
  wizard.draftStorageChanged,
  wizard.reviewRequested,
  wizard.reviewFailed,
  wizard.platformOverrideRequested,
  wizard.platformOverrideFailed,
  wizard.submitRequested
]

/**
 * Drafts are written to browser storage at most every few hundred milliseconds: a debounce would starve
 * while upload progress keeps arriving. A failed save is reported when that changes, so the lab learns
 * the draft lives on the server only.
 */
export const persistDraftsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter((action) => !NOT_PERSISTED.some((skipped) => skipped.match(action)) && draftIdOf(action) !== undefined),
    groupBy((action) => draftIdOf(action) as string),
    mergeMap((perDraft) =>
      perDraft.pipe(
        auditTime(SAVE_INTERVAL_MS),
        withLatestFrom(state$),
        mergeMap(([, state]) => {
          const draft = draftOf(state, perDraft.key)
          const userId = userIdOf(state)
          if (!userId || !draft) {
            return EMPTY
          }
          if (draft.submission) {
            removeDraft(userId, draft.id)
            return EMPTY
          }
          if (!draft.orderId && draft.fileOrder.length === 0) {
            return EMPTY
          }
          const kept = saveDraft(userId, draft)
          return kept === !draft.storageFailed ? EMPTY : of(wizard.draftStorageChanged({ draftId: draft.id, kept }))
        })
      )
    )
  )

export const discardDraftEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.draftDiscarded.match),
    withLatestFrom(state$),
    mergeMap(
      guarded('discarding a draft', ([action, state]) => {
        const userId = userIdOf(state)
        if (userId) {
          removeDraft(userId, action.payload.draftId)
        }
        wizardUploads.discard(action.payload.draftId)
        releaseFileHandles(action.payload.draftId)
        return EMPTY
      })
    )
  )
